import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation, Trans } from 'react-i18next'
import { APIProvider } from '@vis.gl/react-google-maps'
import { useAuthContext } from '@/contexts/auth-context'
import { PlaceSearch, type PlaceDetails } from '@/components/locations/place-search'

const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || ''
import { Button } from '@/components/ui/button'
import { Header } from '@/components/ui/header'
import { createInternalLocation } from '@/lib/api/locations'
import { triggerReviewSync } from '@/lib/api/reviews'
import {
  MapPin,
  Phone,
  Globe,
  Loader2,
  ChevronRight,
  X,
  Search,
  Star,
  TrendingUp,
} from 'lucide-react'

export function LocationSearchPage() {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { user } = useAuthContext()
  const [selectedPlace, setSelectedPlace] = useState<PlaceDetails | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handlePlaceSelect = (place: PlaceDetails) => {
    setSelectedPlace(place)
    setError(null)
  }

  const handleClearSelection = () => {
    setSelectedPlace(null)
  }

  const handleContinue = async () => {
    if (!selectedPlace) return

    console.log('[LocationSearch] Creating location, user:', user?.email, user?.id)

    setIsSubmitting(true)
    setError(null)

    try {
      // Step 1: Create the location in the backend
      console.log('[LocationSearch] Calling createInternalLocation...')
      const { location, created, review_count } = await createInternalLocation({
        name: selectedPlace.name,
        address: selectedPlace.address,
        googleMapsUrl: selectedPlace.googleMapsUrl,
        placeId: selectedPlace.placeId,
        phone: selectedPlace.phone,
        website: selectedPlace.website,
        latitude: selectedPlace.location.lat,
        longitude: selectedPlace.location.lng,
        types: selectedPlace.types,
      })
      console.log('[LocationSearch] Location created:', { location, created, review_count })

      // Step 2: Only trigger sync if no existing reviews
      // The cronjob keeps reviews up to date for locations with existing reviews
      if (review_count === 0) {
        console.log('[LocationSearch] Triggering review sync for location:', location.id)
        await triggerReviewSync(location.id, selectedPlace.googleMapsUrl)
      }

      // Step 3: Redirect to dashboard
      console.log('[LocationSearch] Redirecting to dashboard...')
      navigate('/dashboard')
    } catch (err) {
      console.error('Failed to create location:', err)
      setError(err instanceof Error ? err.message : t('locationSearch.createFailed'))
    } finally {
      setIsSubmitting(false)
    }
  }

  const features = [
    {
      icon: Search,
      title: t('locationSearch.featureFindTitle'),
      description: t('locationSearch.featureFindDesc'),
    },
    {
      icon: Star,
      title: t('locationSearch.featureAggregatedTitle'),
      description: t('locationSearch.featureAggregatedDesc'),
    },
    {
      icon: TrendingUp,
      title: t('locationSearch.featureTrackTitle'),
      description: t('locationSearch.featureTrackDesc'),
    },
  ]

  return (
    <div className="min-h-screen bg-warm-gray flex flex-col">
      {/* Header */}
      <Header />

      {/* Main Content */}
      <div className="flex-1 flex">
        {/* Left Panel - Branding (desktop only) */}
        <div className="hidden lg:flex lg:w-1/2 bg-primary p-12 flex-col justify-between">
          <div /> {/* Spacer */}
          <div className="space-y-6">
            <h1 className="text-4xl xl:text-5xl font-bold text-white leading-tight text-balance">
              {t('locationSearch.heroTitle')}
            </h1>
            <p className="text-lg text-white/80 max-w-md">{t('locationSearch.heroSubtitle')}</p>
          </div>
          <div className="flex items-center gap-3 text-white/80 text-sm">
            <div className="w-2 h-2 rounded-full bg-white/60" />
            <span>{t('locationSearch.syncedWithin24h')}</span>
          </div>
        </div>

        {/* Right Panel - Search */}
        <div className="flex-1 flex flex-col overflow-y-auto">
          <div className="flex-1 flex flex-col justify-center p-8 lg:p-12">
            <div className="max-w-md mx-auto w-full space-y-8">
              <div className="space-y-2">
                <h2 className="text-3xl font-bold text-gray-900">{t('locationSearch.title')}</h2>
                <p className="text-gray-500">{t('locationSearch.subtitle')}</p>
              </div>

              {/* Features */}
              <div className="grid gap-4">
                {features.map((feature) => (
                  <div
                    key={feature.title}
                    className="flex items-start gap-4 p-4 rounded-xl bg-white border border-gray-200"
                  >
                    <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0">
                      <feature.icon className="w-5 h-5 text-accent" />
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900">{feature.title}</h3>
                      <p className="text-sm text-gray-500">{feature.description}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Search Input */}
              <div className="space-y-3">
                <label className="text-sm font-medium text-gray-900">
                  {t('locationSearch.searchLabel')}
                </label>
                <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
                  <PlaceSearch
                    onPlaceSelect={handlePlaceSelect}
                    placeholder={t('locationSearch.searchPlaceholder')}
                    className="[&_input]:h-12 [&_input]:rounded-xl [&_input]:border-border [&_input]:bg-white"
                  />
                </APIProvider>
              </div>

              {/* Selected Place Preview */}
              {selectedPlace && (
                <div className="rounded-xl bg-white border border-gray-200 p-6 space-y-4">
                  <div className="flex items-start justify-between">
                    <h3 className="text-lg font-semibold text-gray-900">{selectedPlace.name}</h3>
                    <button
                      onClick={handleClearSelection}
                      className="text-gray-400 hover:text-gray-700 transition-colors p-1 rounded-lg hover:bg-gray-100"
                      aria-label={t('locationSearch.clearSelection')}
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-start gap-3 text-sm">
                      <MapPin className="h-4 w-4 text-gray-400 mt-0.5 shrink-0" />
                      <span className="text-gray-600">{selectedPlace.address}</span>
                    </div>

                    {selectedPlace.phone && (
                      <div className="flex items-center gap-3 text-sm">
                        <Phone className="h-4 w-4 text-gray-400 shrink-0" />
                        <span className="text-gray-600">{selectedPlace.phone}</span>
                      </div>
                    )}

                    {selectedPlace.website && (
                      <div className="flex items-center gap-3 text-sm">
                        <Globe className="h-4 w-4 text-gray-400 shrink-0" />
                        <a
                          href={selectedPlace.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary hover:underline truncate"
                        >
                          {selectedPlace.website}
                        </a>
                      </div>
                    )}
                  </div>

                  {error && (
                    <div className="p-3 rounded-lg bg-destructive/10 text-destructive text-sm">
                      {error}
                    </div>
                  )}

                  <Button
                    onClick={handleContinue}
                    disabled={isSubmitting}
                    size="lg"
                    className="w-full h-12 text-base font-semibold gap-2 group"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        {t('locationSearch.creatingLocation')}
                      </>
                    ) : (
                      <>
                        {t('locationSearch.addLocationCta')}
                        <ChevronRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                      </>
                    )}
                  </Button>
                </div>
              )}

              {/* Empty State */}
              {!selectedPlace && (
                <div className="text-center py-8">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-4">
                    <MapPin className="h-8 w-8 text-gray-400" />
                  </div>
                  <p className="text-gray-500">{t('locationSearch.emptyStateHint')}</p>
                </div>
              )}

              <p className="text-xs text-center text-gray-400">
                <Trans
                  i18nKey="locationSearch.legal"
                  components={{
                    terms: <a href="#" className="underline hover:text-gray-700" />,
                    privacy: <a href="#" className="underline hover:text-gray-700" />,
                  }}
                />
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
